import React, { useState, useEffect, useCallback } from 'react';
import ReactTable from 'react-table';
import { Button, Alert } from 'reactstrap';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { getMismatches } from '../../services/api/prescription-mismatch';
import DecideModal from './DecideModal';

// ── Badges ─────────────────────────────────────────────────────────────────

const Badge = styled.span`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    white-space: nowrap;
`;

const ACTION_COLORS = {
    REJECT: '#dc3545',
    CLARIFY: '#fc6d3b',
};

const MISMATCH_TYPE_COLORS = {
    patient_name: '#0049C6',
    doctor_name: '#6C5DD3',
    hospital_name: '#00897b',
    appointment_date: '#5c6bc0',
};

const USER_RESPONSE_COLORS = {
    PROCEED_ANYWAY: '#00b479',
    CONFIRMED: '#0049C6',
    CANCELLED: '#dc3545',
};

const MISMATCH_TYPE_LABELS = {
    patient_name: 'Patient Name',
    doctor_name: 'Doctor Name',
    hospital_name: 'Hospital Name',
    appointment_date: 'Appointment Date',
};

// ── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
    if (!iso) return '—';
    return DateTime.fromISO(iso).toFormat('dd MMM yyyy, HH:mm');
};

const ActionBadge = ({ value }) => (
    <Badge style={{ backgroundColor: ACTION_COLORS[value] || '#6c757d' }}>
        {value}
    </Badge>
);

const MismatchTypeBadge = ({ value }) => (
    <Badge style={{ backgroundColor: MISMATCH_TYPE_COLORS[value] || '#6c757d' }}>
        {MISMATCH_TYPE_LABELS[value] || value}
    </Badge>
);

const UserResponseBadge = ({ value }) => {
    if (!value) return <span>—</span>;
    return (
        <Badge style={{ backgroundColor: USER_RESPONSE_COLORS[value] || '#6c757d' }}>
            {value.replace(/_/g, ' ')}
        </Badge>
    );
};

// ── Component ──────────────────────────────────────────────────────────────

const PrescriptionMismatchTable = ({ mismatchType }) => {
    const [page, setPage] = useState(0);
    const [pageSize] = useState(20);
    const [data, setData] = useState([]);
    const [pages, setPages] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [decideModal, setDecideModal] = useState({ isOpen: false, mismatch: null });

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getMismatches({ page, limit: pageSize, mismatchType });
            const filtered = (result.data || []).filter((m) => m.userResponse !== 'CANCELLED');
            setData(filtered);
            setPages(Math.ceil((result.total || 0) / pageSize));
        } catch (err) {
            setError(err.message || 'Failed to load mismatch cases.');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, mismatchType]);

    useEffect(() => {
        setPage(0);
    }, [mismatchType]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openDecideModal = (mismatch) => setDecideModal({ isOpen: true, mismatch });
    const closeDecideModal = () => setDecideModal({ isOpen: false, mismatch: null });

    const columns = [
        {
            Header: 'Patient Name',
            accessor: 'patientName',
            Cell: ({ value }) => value || '—',
        },
        {
            Header: 'Mismatch Types',
            accessor: 'mismatches',
            Cell: ({ value }) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(value || []).map((m, i) => (
                        <MismatchTypeBadge key={i} value={m.mismatchType} />
                    ))}
                </div>
            ),
        },
        {
            Header: 'Action',
            accessor: 'action',
            Cell: ({ value }) => <ActionBadge value={value} />,
        },
        {
            Header: 'User Response',
            accessor: 'userResponse',
            Cell: ({ value }) => <UserResponseBadge value={value} />,
        },
        {
            Header: 'Created At',
            accessor: 'createdAt',
            sortable: true,
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: 'Action',
            sortable: false,
            Cell: ({ original }) => (
                <Button
                    color="primary"
                    size="sm"
                    onClick={() => openDecideModal(original)}
                >
                    Decide
                </Button>
            ),
        },
    ];

    if (error) {
        return (
            <Alert color="danger" className="d-flex align-items-center justify-content-between">
                <span>{error}</span>
                <Button color="danger" outline size="sm" onClick={fetchData}>
                    Retry
                </Button>
            </Alert>
        );
    }

    return (
        <>
            <ReactTable
                data={data}
                columns={columns}
                loading={loading}
                manual
                page={page}
                pages={pages}
                pageSize={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                showPagination
                noDataText={loading ? '' : 'No pending mismatch cases.'}
                className="-striped -highlight"
            />
            <DecideModal
                mismatch={decideModal.mismatch}
                isOpen={decideModal.isOpen}
                onClose={closeDecideModal}
                onSuccess={fetchData}
            />
        </>
    );
};

export default PrescriptionMismatchTable;
